// =====================================================
// Accept or reject tracked changes in a parsed WordprocessingML part, per revision (moved out
// of changes/apply-decisions.ts so the writer can use it too: Compare accepts the drafts'
// own tracked changes in notes, headers and footers with it).
//
// The accept rules are the writer's own (docx-tracking.ts acceptInsDelIn: remove w:del /
// w:moveFrom, unwrap w:ins / w:moveTo), extended per revision and to the other direction:
//   run content    ins/moveTo: accept = unwrap, reject = remove
//                  del/moveFrom: accept = remove, reject = unwrap (delText → t, delInstrText → instrText)
//   paragraph mark ins: accept = drop the marker, reject = join with the next paragraph
//                  del: accept = join with the next paragraph, reject = drop the marker
//   table row      (trPr ins/del) ins: reject = remove the row; del: accept = remove the row
//   table cell     cellIns: reject = remove the cell; cellDel: accept = remove the cell
//   properties     pPrChange/rPrChange/sectPrChange/tblPrChange/trPrChange/tcPrChange/
//                  tblGridChange/tblPrExChange: accept = drop the change record,
//                  reject = restore the properties it records
// A joined paragraph keeps the following paragraph's properties (the mark that survives),
// as in Word. Properties left empty by a decision are removed.
// =====================================================

export const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
export type Decision = 'accept' | 'reject';

export const RUN = new Set(['ins', 'del', 'moveFrom', 'moveTo']);
export const PROP_CHANGE: Record<string, string> = {   // change element → the properties element it records
  pPrChange: 'pPr', rPrChange: 'rPr', sectPrChange: 'sectPr', tblPrChange: 'tblPr', trPrChange: 'trPr',
  tcPrChange: 'tcPr', tblGridChange: 'tblGrid', tblPrExChange: 'tblPrEx',
};
export const CELL = new Set(['cellIns', 'cellDel', 'cellMerge']);
// children of a properties element that are not part of the recorded change
const KEEP_ON_REJECT: Record<string, string[]> = {
  pPr: ['rPr', 'sectPr'],                       // paragraph-mark run properties, section break
  rPr: ['ins', 'del', 'moveFrom', 'moveTo'],    // paragraph-mark revision markers
  sectPr: ['headerReference', 'footerReference'],
};

const kids = (el: Element): Element[] => Array.from(el.childNodes).filter(n => n.nodeType === 1) as Element[];
const isW = (el: Node | null, name?: string) => !!el && el.nodeType === 1 && (el as Element).namespaceURI === W && (!name || (el as Element).localName === name);
export const wAll = (root: Element | Document, local: string) => Array.from(root.getElementsByTagNameNS(W, local)) as Element[];
function attached(el: Node, root: Node): boolean {
  for (let n: Node | null = el; n; n = n.parentNode) if (n === root) return true;
  return false;
}
function unwrap(el: Element) {
  const parent = el.parentNode!;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}
export function remove(el: Element) { el.parentNode?.removeChild(el); }
/** An rPr or pPr left with nothing in it goes (and a pPr emptied by that, too). */
function dropIfEmpty(el: Element) {
  if (!isW(el, 'rPr') && !isW(el, 'pPr')) return;
  if (kids(el).length || Array.from(el.attributes).some(a => !/^xmlns(:|$)/.test(a.name))) return;
  const parent = el.parentNode as Element | null;
  remove(el);
  if (parent && isW(parent, 'pPr')) dropIfEmpty(parent);
}
function rename(el: Element, local: string): Element {
  const n = el.ownerDocument!.createElementNS(W, `w:${local}`);
  for (const a of Array.from(el.attributes)) n.setAttribute(a.name, a.value);
  while (el.firstChild) n.appendChild(el.firstChild);
  el.parentNode!.replaceChild(n, el);
  return n;
}
/** delText → t and delInstrText → instrText below a rejected deletion (not inside a nested one). */
function restoreDeletedText(del: Element) {
  for (const [from, to] of [['delText', 't'], ['delInstrText', 'instrText']]) {
    for (const t of wAll(del, from)) {
      let nested = false;
      for (let n = t.parentNode; n && n !== del; n = n.parentNode) if (isW(n, 'del') || isW(n, 'moveFrom')) { nested = true; break; }
      if (!nested) rename(t, to);
    }
  }
}

/** Where a revision element sits: run content, a paragraph mark, a table row marker, a property record or a cell marker. */
function levelOf(el: Element): 'run' | 'mark' | 'row' | 'prop' | 'cell' | 'other' {
  const ln = el.localName;
  if (ln in PROP_CHANGE) return 'prop';
  if (CELL.has(ln)) return 'cell';
  if (!RUN.has(ln)) return 'other';
  const p = el.parentNode as Element | null;
  if (isW(p, 'rPr') && isW(p!.parentNode, 'pPr')) return 'mark';
  if (isW(p, 'trPr')) return 'row';
  if (isW(p, 'rPr')) return 'other';   // a run's own rPr carries no ins/del in valid OOXML
  return 'run';
}

/** Join paragraph `p` with the paragraph after it. A paragraph's properties live in its mark,
 *  and `p`'s mark is the one going, so the joined paragraph is the next one (its properties
 *  and any changes recorded on them) with `p`'s content in front, as in Word. */
function joinWithNext(p: Element): boolean {
  // the next block, past markers that sit between paragraphs (bookmark / comment-range /
  // permission / move-range starts and ends, proofing marks): Word joins across them
  const between = /^(bookmarkStart|bookmarkEnd|commentRangeStart|commentRangeEnd|permStart|permEnd|proofErr|moveFromRangeStart|moveFromRangeEnd|moveToRangeStart|moveToRangeEnd)$/;
  let next = p.nextSibling;
  while (next && (next.nodeType !== 1 || (isW(next) && between.test((next as Element).localName)))) next = next.nextSibling;
  if (!isW(next, 'p')) {
    // Nothing to join with. When a table follows (outside a table cell), a paragraph left with
    // no content goes, as in Word; at the end of a cell or of the body it stays (one is needed).
    const hasContent = kids(p).some(c => !isW(c, 'pPr') && !/^(bookmarkStart|bookmarkEnd|commentRangeStart|commentRangeEnd|proofErr|permStart|permEnd)$/.test(c.localName));
    const sectionBreak = kids(p).some(c => isW(c, 'pPr') && kids(c).some(k => isW(k, 'sectPr')));
    if (!isW(next, 'tbl') || isW(p.parentNode, 'tc') || hasContent || sectionBreak) return false;
    remove(p);
    return true;
  }
  const q = next as Element;
  let at: Node | null = q.firstChild;
  while (at && isW(at, 'pPr')) at = at.nextSibling;
  for (const c of Array.from(p.childNodes)) if (!isW(c, 'pPr')) q.insertBefore(c, at);
  remove(p);
  return true;
}

export interface DecideResult { applied: number; joins: number; notes: string[]; }

/**
 * Apply decisions to every revision element below `root` (a whole part, e.g. the parsed
 * word/document.xml). `decide(id)` returns 'accept', 'reject' or null (leave tracked).
 */
export function decideRevisions(root: Document | Element, decide: (id: number, el: Element) => Decision | null): DecideResult {
  const res: DecideResult = { applied: 0, joins: 0, notes: [] };
  const top = (root as Document).documentElement || root;
  // every revision element, in document order (an outer revision before one nested in it)
  const els: Element[] = [];
  const walk = (n: Node) => {
    for (let c = n.firstChild; c; c = c.nextSibling) {
      if (c.nodeType !== 1) continue;
      const e = c as Element;
      if (e.namespaceURI === W && (RUN.has(e.localName) || e.localName in PROP_CHANGE || CELL.has(e.localName))) els.push(e);
      walk(e);
    }
  };
  walk(top);
  const joins: Element[] = [];
  for (const el of els) {
    if (!attached(el, top)) continue;   // inside something already removed
    const id = el.getAttribute('w:id');
    const d = id == null ? null : decide(Number(id), el);
    if (!d) continue;
    const ln = el.localName;
    const level = levelOf(el);
    const ins = ln === 'ins' || ln === 'moveTo' || ln === 'cellIns';
    res.applied++;
    if (level === 'run') {
      if (ins) { if (d === 'accept') unwrap(el); else remove(el); }
      else { if (d === 'accept') remove(el); else { restoreDeletedText(el); unwrap(el); } }
    } else if (level === 'mark') {
      const p = el.parentNode!.parentNode!.parentNode as Element;   // rPr → pPr → p
      const markRPr = el.parentNode as Element;
      remove(el);
      dropIfEmpty(markRPr);   // nothing left to say about the mark: no empty rPr / pPr behind (as Word)
      if ((ins && d === 'reject') || (!ins && d === 'accept')) joins.push(p);
    } else if (level === 'row') {
      const tr = el.parentNode!.parentNode as Element;   // trPr → tr
      if ((ins && d === 'reject') || (!ins && d === 'accept')) remove(tr); else remove(el);
    } else if (level === 'cell') {
      const tc = el.parentNode!.parentNode as Element;   // tcPr → tc
      if (ln === 'cellMerge') remove(el);
      else if ((ins && d === 'reject') || (!ins && d === 'accept')) remove(tc); else remove(el);
    } else if (level === 'prop') {
      const holder = el.parentNode as Element;
      if (d === 'reject') {
        const old = kids(el).find(c => c.localName === PROP_CHANGE[ln]);
        const keep = KEEP_ON_REJECT[holder.localName] || [];
        for (const c of kids(holder)) if (c !== el && !keep.includes(c.localName)) remove(c);
        // pPr: the recorded properties come before the mark's rPr and the sectPr; elsewhere
        // the kept children (rPr revision markers, header/footer references) come first
        const anchor = holder.localName === 'pPr' ? (kids(holder).find(c => keep.includes(c.localName)) || null) : null;
        if (old) for (const c of kids(old)) holder.insertBefore(c, anchor);
      }
      remove(el);
      dropIfEmpty(holder);
    } else {
      res.applied--;
      res.notes.push(`revision ${id} (<w:${ln}>) is in an unexpected place; left as is`);
    }
  }
  // paragraph joins last, from the end, so a run of joins collapses into one paragraph
  for (const p of joins.reverse()) {
    if (!attached(p, top)) continue;
    if (joinWithNext(p)) res.joins++;
    else res.notes.push('a paragraph mark at the end of a table cell or section could not be joined; the break is kept');
  }
  return res;
}
